import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Link2, CheckCircle } from 'lucide-react';
import { useScreenInit } from '../useScreenInit.js';
type OnboardingStep = 'profilo' | 'proprieta' | 'inserzioni' | 'vikey' | 'done';
export function OnboardingPage() {
  const navigate = useNavigate();
  const screenInit = useScreenInit() as {
    step?: OnboardingStep;
  };
  const [step, setStep] = useState<OnboardingStep>(screenInit?.step ?? 'profilo');
  // Form state
  const [nome, setNome] = useState('');
  const [numProprieta, setNumProprieta] = useState('');
  const [usoVikey, setUsoVikey] = useState<'si' | 'no' | null>(null);
  const [propNome, setPropNome] = useState('');
  const [propIndirizzo, setPropIndirizzo] = useState('');
  const [propCitta, setPropCitta] = useState('');
  const [propMq, setPropMq] = useState('');
  const [propPiano, setPropPiano] = useState('');
  const [propAscensore, setPropAscensore] = useState(false);
  const [propNote, setPropNote] = useState('');
  const [linkInserzione, setLinkInserzione] = useState('');
  const [importedListings, setImportedListings] = useState<Array<{
    title: string;
    source: string;
  }>>([]);
  const [connectedVikey, setConnectedVikey] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const stepIndex = {
    profilo: 1,
    proprieta: 2,
    inserzioni: 3,
    vikey: 4,
    done: 5
  };
  const skipOnboarding = () => {
    localStorage.setItem('cleanai_onboarded', '1');
    navigate('/dashboard');
  };
  const handleNext = () => {
    if (step === 'profilo') setStep('proprieta');else if (step === 'proprieta') setStep('inserzioni');else if (step === 'inserzioni') setStep('vikey');else if (step === 'vikey') setStep('done');
  };
  const handleBack = () => {
    if (step === 'proprieta') setStep('profilo');else if (step === 'inserzioni') setStep('proprieta');else if (step === 'vikey') setStep('inserzioni');
  };
  const mockImport = () => {
    if (!linkInserzione) return;
    setImportedListings([...importedListings, {
      title: 'Villa Sunset · Milano centro',
      source: 'Airbnb'
    }]);
    setLinkInserzione('');
  };
  const mockConnectVikey = () => {
    setIsConnecting(true);
    setTimeout(() => {
      setIsConnecting(false);
      setConnectedVikey(true);
      setTimeout(() => {
        setStep('done');
      }, 1500);
    }, 1500);
  };
  const isProfiloValid = nome.trim() !== '';
  const isProprietaValid = propNome.trim() !== '' && propIndirizzo.trim() !== '' && propCitta.trim() !== '';
  return <div className="min-h-screen bg-background flex flex-col" data-id="element-3761">
      {/* Header sticky */}
      <header className="bg-white border-b border-border sticky top-0 z-10" data-id="element-3762">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 py-4 flex items-center justify-between gap-4" data-id="element-3763">
          {/* Left: Skip */}
          <button onClick={skipOnboarding} className="text-xs font-medium text-muted-foreground hover:text-foreground border border-border rounded-full px-3 py-1.5 transition-colors hover:bg-muted" data-id="element-3764">
            Salta (demo) →
          </button>

          {/* Right: Passo */}
          {step !== 'done' && <span className="text-xs text-muted-foreground" data-id="element-3765">
              Passo {stepIndex[step]} di 4
            </span>}
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-4 lg:px-8 py-8 lg:py-12" data-id="element-3766">
        {/* Progress bar 4 segmenti */}
        {step !== 'done' && <div className="flex items-center gap-2 mb-8" data-id="element-3767">
            {(['profilo', 'proprieta', 'inserzioni', 'vikey'] as const).map((s, i) => <div key={s} className={`h-1 flex-1 rounded-full ${i < stepIndex[step] ? 'bg-coral' : 'bg-muted'}`} data-id="element-3768" />)}
          </div>}

        {/* Step 1: Profilo */}
        {step === 'profilo' && <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-3769">
            <h1 className="lg:text-3xl text-navy mb-2 text-[28px] font-[600]" data-id="element-3770">
              Benvenuto su CleanAI
            </h1>
            <p className="text-sm lg:text-base text-muted-foreground mb-8" data-id="element-3771">
              Le pulizie di turnover, gestite per te. Iniziamo dal capire chi
              sei.
            </p>

            <div className="bg-white border border-border rounded-xl p-5 lg:p-6 mb-6 space-y-5" data-id="element-3772">
              <div data-id="element-3773">
                <label className="block text-sm font-medium text-foreground mb-1.5" data-id="element-3774">
                  Come ti chiamiamo *
                </label>
                <input type="text" value={nome} onChange={e => setNome(e.target.value)} placeholder="Es. Massimo" className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none bg-white min-h-[44px]" data-id="element-3775" />
              </div>

              <div data-id="element-3776">
                <label className="block text-sm font-medium text-foreground mb-1.5" data-id="element-3777">
                  Quante proprietà gestisci? *
                </label>
                <input type="number" value={numProprieta} onChange={e => setNumProprieta(e.target.value)} placeholder="Es. 3" className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none bg-white min-h-[44px]" data-id="element-3778" />
              </div>

              <div data-id="element-3779">
                <label className="block text-sm font-medium text-foreground mb-2" data-id="element-3780">
                  Sei già su Vikey?
                </label>
                <div className="flex gap-3" data-id="element-3781">
                  <button onClick={() => setUsoVikey('si')} className={`flex-1 py-2.5 px-4 rounded-lg border text-sm font-medium transition-colors ${usoVikey === 'si' ? 'border-coral bg-coral/5 text-coral' : 'border-border bg-white text-foreground hover:bg-muted'}`} data-id="element-3782">
                    Sì, uso Vikey
                  </button>
                  <button onClick={() => setUsoVikey('no')} className={`flex-1 py-2.5 px-4 rounded-lg border text-sm font-medium transition-colors ${usoVikey === 'no' ? 'border-coral bg-coral/5 text-coral' : 'border-border bg-white text-foreground hover:bg-muted'}`} data-id="element-3783">
                    No, ancora no
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-4" data-id="element-3784">
              <button disabled className="px-6 py-2.5 text-sm font-medium text-muted-foreground opacity-50 cursor-not-allowed" data-id="element-3785">
                Indietro
              </button>
              <button onClick={handleNext} disabled={!isProfiloValid} className="bg-coral text-white rounded-xl px-6 py-2.5 text-sm font-semibold hover:bg-coral-600 transition-colors shadow-sm min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3786">
                Continua
              </button>
            </div>
          </div>}

        {/* Step 2: Proprietà */}
        {step === 'proprieta' && <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-3787">
            <h1 className="text-2xl lg:text-3xl font-bold text-navy mb-2" data-id="element-3788">
              Aggiungi la tua prima proprietà
            </h1>
            <p className="text-sm lg:text-base text-muted-foreground mb-8" data-id="element-3789">
              Inserisci i dati base. Potrai completare la Scheda Proprietà con
              foto e dettagli più avanti.
            </p>

            <div className="bg-white border border-border rounded-xl p-5 lg:p-6 mb-6 space-y-5" data-id="element-3790">
              <div data-id="element-3791">
                <label className="block text-sm font-medium text-foreground mb-1.5" data-id="element-3792">
                  Nome immobile *
                </label>
                <input type="text" value={propNome} onChange={e => setPropNome(e.target.value)} placeholder="Es. Villa Sunset" className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none bg-white min-h-[44px]" data-id="element-3793" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-3794">
                <div data-id="element-3795">
                  <label className="block text-sm font-medium text-foreground mb-1.5" data-id="element-3796">
                    Indirizzo *
                  </label>
                  <input type="text" value={propIndirizzo} onChange={e => setPropIndirizzo(e.target.value)} placeholder="Es. Via dei Giardini 12" className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none bg-white min-h-[44px]" data-id="element-3797" />
                </div>
                <div data-id="element-3798">
                  <label className="block text-sm font-medium text-foreground mb-1.5" data-id="element-3799">
                    Città *
                  </label>
                  <input type="text" value={propCitta} onChange={e => setPropCitta(e.target.value)} placeholder="Es. Milano" className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none bg-white min-h-[44px]" data-id="element-3800" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-5" data-id="element-3801">
                <div data-id="element-3802">
                  <label className="block text-sm font-medium text-foreground mb-1.5" data-id="element-3803">
                    Metratura (mq)
                  </label>
                  <input type="number" value={propMq} onChange={e => setPropMq(e.target.value)} placeholder="Es. 85" className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none bg-white min-h-[44px]" data-id="element-3804" />
                </div>
                <div data-id="element-3805">
                  <label className="block text-sm font-medium text-foreground mb-1.5" data-id="element-3806">
                    Piano
                  </label>
                  <input type="text" value={propPiano} onChange={e => setPropPiano(e.target.value)} placeholder="Es. 3" className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none bg-white min-h-[44px]" data-id="element-3807" />
                </div>
                <div data-id="element-3808">
                  <label className="block text-sm font-medium text-foreground mb-1.5" data-id="element-3809">
                    Ascensore
                  </label>
                  <div className="w-full min-h-[44px] flex items-center justify-between gap-3 px-4 py-2.5 border border-border rounded-lg bg-white" data-id="element-3810">
                    <span className={`text-sm font-medium transition-colors ${propAscensore ? 'text-coral' : 'text-muted-foreground'}`} data-id="element-3811">
                      {propAscensore ? 'Sì' : 'No'}
                    </span>
                    <button type="button" role="switch" aria-checked={propAscensore} aria-label="Ascensore disponibile" onClick={() => setPropAscensore(!propAscensore)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-coral focus:ring-offset-2 flex-shrink-0 ${propAscensore ? 'bg-coral' : 'bg-gray-300'}`} data-id="element-3812">
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${propAscensore ? 'translate-x-5' : 'translate-x-0.5'}`} data-id="element-3813" />
                    </button>
                  </div>
                </div>
              </div>

              <div data-id="element-3814">
                <label className="block text-sm font-medium text-foreground mb-1.5" data-id="element-3815">
                  Note particolari
                </label>
                <textarea value={propNote} onChange={e => setPropNote(e.target.value)} placeholder="Es. Citofono rotto, chiamare al cellulare" rows={2} className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none bg-white resize-none" data-id="element-3816" />
              </div>
            </div>

            <div className="flex items-center justify-between pt-4" data-id="element-3817">
              <button onClick={handleBack} className="px-6 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors" data-id="element-3818">
                Indietro
              </button>
              <button onClick={handleNext} disabled={!isProprietaValid} className="bg-coral text-white rounded-xl px-6 py-2.5 text-sm font-semibold hover:bg-coral-600 transition-colors shadow-sm min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3819">
                Continua
              </button>
            </div>
          </div>}

        {/* Step 3: Inserzioni */}
        {step === 'inserzioni' && <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-3820">
            <h1 className="text-2xl lg:text-3xl font-bold text-navy mb-2" data-id="element-3821">
              Collega le tue inserzioni
            </h1>
            <p className="text-sm lg:text-base text-muted-foreground mb-6" data-id="element-3822">
              Incolla il link di un'inserzione Airbnb, Booking o Expedia:
              CleanAI precompila i dati e sincronizza i check-in/out.
            </p>

            <div className="bg-coral/5 border border-coral/20 rounded-xl p-4 flex items-start gap-3 mb-6" data-id="element-3823">
              <Sparkles className="text-coral flex-shrink-0 mt-0.5" size={18} data-id="element-3824" />
              <p className="text-xs text-foreground leading-relaxed" data-id="element-3825">
                Per gli host già su Vikey, i check-in/out arrivano in automatico
                dal channel manager — vai pure avanti.
              </p>
            </div>

            <div className="bg-white border border-border rounded-xl p-5 lg:p-6 mb-6" data-id="element-3826">
              <div className="flex gap-3 items-end mb-6" data-id="element-3827">
                <div className="flex-1" data-id="element-3828">
                  <label className="block text-sm font-medium text-foreground mb-1.5" data-id="element-3829">
                    Link inserzione
                  </label>
                  <div className="relative" data-id="element-3830">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3831">
                      <Link2 size={16} className="text-muted-foreground" data-id="element-3832" />
                    </div>
                    <input type="text" value={linkInserzione} onChange={e => setLinkInserzione(e.target.value)} placeholder="https://www.airbnb.com/rooms/..." className="w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none bg-white min-h-[44px]" data-id="element-3833" />
                  </div>
                </div>
                <button onClick={mockImport} disabled={!linkInserzione} className="bg-navy text-white rounded-lg px-6 py-2.5 text-sm font-semibold hover:bg-navy-light transition-colors shadow-sm min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3834">
                  Importa
                </button>
              </div>

              {importedListings.length > 0 && <div className="space-y-3" data-id="element-3835">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider" data-id="element-3836">
                    Inserzioni importate
                  </h3>
                  {importedListings.map((l, i) => <div key={i} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border border-border" data-id="element-3837">
                      <CheckCircle size={16} className="text-emerald-600 flex-shrink-0" data-id="element-3838" />
                      <span className="text-sm font-medium text-navy truncate" data-id="element-3839">
                        {l.title}
                      </span>
                      <span className="text-xs text-muted-foreground ml-auto flex-shrink-0 bg-white px-2 py-1 rounded border border-border" data-id="element-3840">
                        {l.source}
                      </span>
                    </div>)}
                </div>}
            </div>

            <div className="flex items-center justify-between pt-4" data-id="element-3841">
              <button onClick={handleBack} className="px-6 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors" data-id="element-3842">
                Indietro
              </button>
              <button onClick={handleNext} className="bg-coral text-white rounded-xl px-6 py-2.5 text-sm font-semibold hover:bg-coral-600 transition-colors shadow-sm min-h-[44px]" data-id="element-3843">
                {importedListings.length > 0 ? 'Continua' : 'Salta e continua'}
              </button>
            </div>
          </div>}

        {/* Step 4: Vikey */}
        {step === 'vikey' && <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-3844">
            <h1 className="text-2xl lg:text-3xl font-bold text-navy mb-2" data-id="element-3845">
              Sincronizza con Vikey
            </h1>
            <p className="text-sm lg:text-base text-muted-foreground mb-8" data-id="element-3846">
              Se usi già Vikey, collegalo ora: check-in/out arrivano
              automaticamente, niente inserimento manuale.
            </p>

            <div className="bg-gradient-to-br from-[#1db6e4]/10 to-[#1db6e4]/5 border-2 border-[#1db6e4]/30 rounded-2xl p-6 lg:p-10 text-center mb-6 relative overflow-hidden" data-id="element-3847">
              {/* Decorative background elements */}
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-[#1db6e4]/15 rounded-full blur-2xl" data-id="element-3848"></div>
              <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-[#1db6e4]/10 rounded-full blur-2xl" data-id="element-3849"></div>

              <div className="relative z-10" data-id="element-3850">
                <img src="https://cdn.magicpatterns.com/uploads/vYGKcY2gotAm5Nn5uTMftD/pasted-image.png" alt="Vikey" className="w-16 h-16 mx-auto mb-5 rounded-xl object-contain shadow-sm" data-id="element-3851" />
                <h2 className="text-xl font-bold text-navy mb-3" data-id="element-3852">
                  Collega Vikey
                </h2>
                <p className="text-sm text-muted-foreground mb-8 max-w-md mx-auto leading-relaxed" data-id="element-3853">
                  Auto-fetch dei check-in/out, sincronizzazione delle
                  prenotazioni, suggerimenti turnover automatici.
                </p>

                {connectedVikey ? <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 px-6 py-3 rounded-full text-sm font-semibold animate-in zoom-in duration-300" data-id="element-3854">
                    <CheckCircle size={18} data-id="element-3855" />
                    Connesso a Vikey · 1 immobile sincronizzato
                  </div> : <button onClick={mockConnectVikey} disabled={isConnecting} className="bg-[#1db6e4] text-white rounded-full px-8 py-3 text-sm font-semibold hover:bg-[#1aa3cc] transition-all shadow-sm min-h-[44px] w-full sm:w-auto disabled:opacity-70 flex items-center justify-center gap-2 mx-auto" data-id="element-3856">
                    {isConnecting ? <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-3857"></div>
                        Connessione in corso...
                      </> : 'Connetti Vikey →'}
                  </button>}

                {!connectedVikey && <p className="text-[11px] text-muted-foreground mt-4" data-id="element-3858">
                    Sarai reindirizzato a Vikey per autorizzare l'integrazione.
                  </p>}
              </div>
            </div>

            <div className="flex items-center justify-between pt-4" data-id="element-3859">
              <button onClick={handleBack} disabled={isConnecting || connectedVikey} className="px-6 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50" data-id="element-3860">
                Indietro
              </button>

              {!connectedVikey && !isConnecting && <button onClick={handleNext} className="text-sm font-medium text-muted-foreground hover:text-foreground underline px-6 py-2.5" data-id="element-3861">
                  Connetterò Vikey più tardi
                </button>}
            </div>
          </div>}

        {/* Step 5: Done */}
        {step === 'done' && <div className="animate-in fade-in zoom-in-95 duration-500 text-center py-12 lg:py-20" data-id="element-3862">
            <div className="w-20 h-20 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-6 border border-emerald-200" data-id="element-3863">
              <CheckCircle size={36} className="text-emerald-600" data-id="element-3864" />
            </div>
            <h1 className="text-2xl lg:text-3xl font-bold text-navy mb-3" data-id="element-3865">
              Tutto pronto, {nome || 'Host'}!
            </h1>
            <p className="text-sm lg:text-base text-muted-foreground mb-10 max-w-md mx-auto leading-relaxed" data-id="element-3866">
              {connectedVikey ? 'Vikey è connesso e sincronizziamo già i tuoi check-in/out. Vai alla Dashboard per vedere il prossimo turnover proposto.' : 'Hai configurato la tua prima proprietà. Vai alla Dashboard per organizzare le pulizie.'}
            </p>
            <button onClick={skipOnboarding} className="bg-coral text-white rounded-xl px-8 py-3 text-base font-semibold hover:bg-coral-600 transition-colors shadow-sm min-h-[48px]" data-id="element-3867">
              Vai alla Dashboard →
            </button>
          </div>}
      </main>
    </div>;
}